import type { AzureComponents } from "@p0security/integrations-azure-shared/components";
import { toScope } from "@p0security/integrations-azure-shared/helper";
import type { AzureIntegration } from "@p0security/integrations-azure-shared/types";
import {
  ENTRA_ID_CALLER_APP_PERMISSIONS,
  MICROSOFT_GRAPH_API_ID,
  toPermissionDescription,
  toPermissionId,
} from "@p0security/integrations-entra-id-shared";
import type {
  FrontendInstallContext,
  FrontendInstaller,
} from "@p0security/integrations-installer";
import { SINGLETON_ITEM } from "@p0security/shared/install/constants";
import { installedItems } from "@p0security/shared/install/installed";
import type { Instructions } from "@p0security/shared/install/types";
import { Typography } from "@p0security/ui";
import type { ReactNode } from "react";

import { appRegistrationPreqMessages } from "./util";

const maxPermissionLength = Math.max(
  ...ENTRA_ID_CALLER_APP_PERMISSIONS.map((permission) => permission.length)
);

const azureIamAssessmentInstructionsFor = (
  context: FrontendInstallContext<AzureIntegration>,
  id: string
): Instructions<ReactNode> => {
  const azureApp = installedItems("azure-app", context.config);
  const { clientId, clientServicePrincipalId } = azureApp[SINGLETON_ITEM] ?? {};
  if (!clientId || !clientServicePrincipalId) {
    throw new Error("Azure App Registration is not installed.");
  }
  const shell = [
    {
      header:
        "Grant Microsoft Graph read permissions to the P0 app registration",
      command: `GRAPH_API_ID="${MICROSOFT_GRAPH_API_ID}"
PERMISSION_IDS=(
${ENTRA_ID_CALLER_APP_PERMISSIONS.map(
  (permission) => `    "${toPermissionId(permission)}"`
).join("\n")}
)

for permission_id in "\${PERMISSION_IDS[@]}"; do
    az ad app permission add \\
        --id "${clientId}" \\
        --api "$GRAPH_API_ID" \\
        --api-permissions "\${permission_id}=Role"
done

az ad app permission admin-consent --id ${clientId}`,
    },
    {
      header:
        "Create role assignment of service principal to Reader Role in the current subscription",
      command: `az role assignment create --assignee ${clientId} --role "Reader" --scope ${toScope(id)}`,
    },
  ];
  const iac = [
    {
      header:
        "Grant Microsoft Graph read permissions to the P0 app registration",
      command: `locals {
  p0_graph_permissions = {
${ENTRA_ID_CALLER_APP_PERMISSIONS.map(
  (permission) =>
    `    "${permission}"${" ".repeat(
      maxPermissionLength - permission.length
    )} = "${toPermissionId(permission)}"`
).join("\n")}
  }
}

data "azuread_application" "p0_azure_app" {
  client_id = "${clientId}"
}

data "azuread_service_principal" "msgraph" {
  client_id = "${MICROSOFT_GRAPH_API_ID}"
}

resource "azuread_application_api_access" "p0_msgraph" {
  application_id = data.azuread_application.p0_azure_app.id
  api_client_id  = "${MICROSOFT_GRAPH_API_ID}"
  role_ids       = values(local.p0_graph_permissions)
}

# Grant admin consent for each permission
resource "azuread_app_role_assignment" "p0_msgraph" {
  for_each            = local.p0_graph_permissions
  app_role_id         = each.value
  principal_object_id = "${clientServicePrincipalId}"
  resource_object_id  = data.azuread_service_principal.msgraph.object_id
}`,
    },
    {
      header:
        "Create role assignment of service principal to Reader Role in the current subscription",
      command: `resource "azurerm_role_assignment" "p0_assessment_reader" {
  scope                = "${toScope(id)}"
  role_definition_name = "Reader"
  principal_id         = "${clientServicePrincipalId}"
}`,
    },
  ];

  return {
    help: (
      <>
        <Typography.Paragraph>
          In order to assess your Azure subscription, P0 needs{" "}
          <Typography.Text code>Reader</Typography.Text> on the subscription and
          the following read-only Microsoft Graph permissions (to inventory
          directory identities and app credentials for principals with role
          assignments):
        </Typography.Paragraph>
        <ul>
          {ENTRA_ID_CALLER_APP_PERMISSIONS.map((permission) => (
            <li key={permission}>
              <Typography.Text code>{permission}</Typography.Text> -{" "}
              {toPermissionDescription(permission)}
            </li>
          ))}
        </ul>
      </>
    ),
    commands: {
      shell,
      iac,
    },
  };
};

export const azureIamAssessmentInstaller: FrontendInstaller<
  typeof AzureComponents
>["components"]["iam-assessment"] = {
  prerequisiteMessages: appRegistrationPreqMessages,
  instructions: azureIamAssessmentInstructionsFor,
  items: {},
};
